import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import './NotificationList.css';

export default function NotificationList({ onClose, onNotificationRead }) {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const panelRef = useRef();

  useEffect(() => {
    if (!currentUser) return;
    const fetchNotifications = async () => {
      setLoading(true);
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const notifs = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      setNotifications(notifs);
      setLoading(false);
      // Mark all as read
      notifs.forEach(async notif => {
        if (!notif.isRead) {
          await updateDoc(doc(db, 'notifications', notif.id), { isRead: true });
        }
      });
    };
    fetchNotifications();
  }, [currentUser]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        if (onClose) onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleNotifClick = async notif => {
    if (!notif.isRead) {
      try {
        await updateDoc(doc(db, 'notifications', notif.id), { isRead: true });
        if (onNotificationRead) {
          onNotificationRead();
        }
      } catch (e) {
        console.error('Failed to mark notification as read:', e);
        return;
      }
    }
    if (notif.type === 'feedback' || notif.type === 'feedback-received') {
      try {
        const q = query(
          collection(db, 'notifications'),
          where('userId', '==', notif.userId),
          where('dinnerId', '==', notif.dinnerId),
          where('type', '==', notif.type)
        );
        const snapshot = await getDocs(q);
        for (const docSnap of snapshot.docs) {
          await deleteDoc(doc(db, 'notifications', docSnap.id));
        }
      } catch (e) {
        console.error('Failed to delete feedback notifications:', e);
      }
    }
    if (notif.link) {
      navigate(notif.link);
      if (onClose) onClose();
    } else if (notif.type === 'reminder') {
      navigate('/profile?tab=pending&pendingTab=host');
      if (onClose) onClose();
    }
  };

  return (
    <div className="notification-list-panel" ref={panelRef}>
      <div className="notification-list-header">
        Notifications
        <button className="notification-list-close" onClick={e => { e.stopPropagation(); console.log('NotificationList close clicked'); if (onClose) onClose(); }}>×</button>
      </div>
      <div className="notification-list-body">
        {loading ? (
          <div className="notification-list-loading">Loading...</div>
        ) : notifications.length === 0 ? (
          <div className="notification-list-empty">No notifications</div>
        ) : (
          notifications
            .filter((notif, idx, arr) => {
              if (notif.type !== 'feedback' && notif.type !== 'feedback-received') return true;
              if (notif.dinnerId) {
                const firstIdx = arr.findIndex(n => n.type === notif.type && n.dinnerId === notif.dinnerId);
                return idx === firstIdx;
              } else {
                const firstIdx = arr.findIndex(n => n.type === notif.type && n.message === notif.message);
                return idx === firstIdx;
              }
            })
            .slice(0, 5)
            .map(notif => (
              <div
                key={notif.id}
                className={`notification-list-item${notif.isRead ? '' : ' unread'}`}
                onClick={() => handleNotifClick(notif)}
                style={{ cursor: notif.link ? 'pointer' : 'default', fontSize: '0.95em' }}
              >
                <div className="notification-list-item-text">
                  {notif.message || notif.text || 'You have a pending dinner request!'}
                </div>
                <div className="notification-list-item-date" style={{ fontSize: '0.85em' }}>
                  {notif.createdAt ? new Date(notif.createdAt).toLocaleString() : ''}
                </div>
              </div>
            ))
        )}
      </div>
    </div>
  );
} 